import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { monitoringApi, Classification, MonitoringApiError, ListParams } from '../../../../services/monitoringApi';

export interface MonitoringListFilters {
    riskLevel: string;
    active: string;
    search: string;
    personName: string;
    hasDivergence: string;
    dateFrom: string;
    dateTo: string;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC' | '';
}

export default function useMonitoringList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<Classification[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const [filters, setFilters] = useState<MonitoringListFilters>({
        riskLevel: '',
        active: 'true',
        search: '',
        personName: '',
        hasDivergence: '',
        dateFrom: '',
        dateTo: '',
        sortBy: '',
        sortOrder: '',
    });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params: ListParams = {
                page,
                pageSize,
                riskLevel: filters.riskLevel || undefined,
                active: filters.active === '' ? undefined : filters.active === 'true',
                search: filters.search ? filters.search.replace(/[.\-/\s]/g, '').toUpperCase() : undefined,
                personName: filters.personName || undefined,
                hasDivergence: filters.hasDivergence === '' ? undefined : filters.hasDivergence === 'true',
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                sortBy: filters.sortBy || undefined,
                sortOrder: filters.sortOrder || undefined,
            };
            const result = await monitoringApi.list(params);
            setRows(result.data?.items || []);
            setTotal(result.data?.total || 0);
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                if (e.status === 403) { navigate('/denied'); return; }
                if (e.status === 401) { navigate('/login'); return; }
            }
            toast.error('Erro ao carregar lista de monitoramento');
            setRows([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, pageSize, filters, navigate, refreshKey]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const refresh = () => setRefreshKey(k => k + 1);

    const updateFilter = (key: keyof MonitoringListFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({
            riskLevel: '',
            active: '',
            search: '',
            personName: '',
            hasDivergence: '',
            dateFrom: '',
            dateTo: '',
            sortBy: '',
            sortOrder: '',
        });
        setPage(1);
        setRefreshKey(k => k + 1);
    };

    const toggleSort = (field: string) => {
        setFilters(prev => {
            if (prev.sortBy === field) {
                // Same column: toggle direction
                const nextOrder = prev.sortOrder === 'ASC' ? 'DESC' : 'ASC';
                return { ...prev, sortOrder: nextOrder };
            }
            // Different column: set ASC
            return { ...prev, sortBy: field, sortOrder: 'ASC' };
        });
        setPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return {
        rows,
        total,
        page,
        setPage,
        pageSize,
        totalPages,
        isLoading,
        initialLoading,
        filters,
        updateFilter,
        clearFilters,
        refresh,
        toggleSort,
    };
}
